import $ from 'jquery'
import { goalbookUrls, toolkitUrls } from '@/links'
import { gradeInstructionalLevels } from '@/util/gradelevels'
import { LICENSE_SUB_TYPES } from '@/store/user'
import {
  SEL_DEFAULT_AREA_PLW,
  SEL_REPLACEMENT_STRATEGIES_PLW,
} from '@/contentprofile/no_sel_mixins'
import { getHandleRequest, getCookie } from './request_handler'

export {
  remapGoalSubject,
  remapStrategySubject,
  getInstructionalLevel,
  getAreasOfNeed,
  getGradesSet,
  getEnrolledGradesSet,
  getGradeLabelForGradeLevel,
  populatePlaceholderText,
  getSkillTypes,
  getSkillBySubject,
  getImpactBySubject,
  getStatementsBySkill,
  postPresentLevelStudent,
  getPresentLevelStudentStatement,
  postPresentLevelStudentStatement,
  deletePresentLevelStudentStatement,
  deleteAllPresentLevelStudentStatementsForSkill,
  getPresentLevelStudentStatementDraft,
  getSharedPresentLevelStudentStatement,
  putPresentLevelStudentStatementSelectStatus,
  returnGoalSubjectForPLWSubject,
  returnGoalDomainsForPLWSkill,
  returnGradeFromEnrolledGrade,
  subjectHasGradeFiltersForAlignedGoals,
  getEffectStatementsBySubjectAndGrade,
  getSkillForEffectStatement,
}

const skillTypes = Object.freeze({
  IMPACT: Symbol('Impact'),
  SKILL: Symbol('Skill'),
})

export const UNLOCKED_IMPACT_UUIDS = [
  '6ea143f7-8684-4f7a-befc-867e173ac1f9',
  '9336eb44-a44b-4a54-ba5c-4282f4e485d8',
  '430af7fe-2236-4db0-b2a3-235659e1e074',
]

export const DEFAULT_ALT_SKILL_LEVEL = 'Target'

const preAcademics = 'Pre-Academics';
const altAcademics = 'Alt Academics';
const lifeSkills = 'Life Skills'
const autism = 'Autism';
const dyslexia = 'Dyslexia';
const executiveFunctioning = 'Executive Functioning'
const behaviorSEL = 'Behavior & SEL';
const englishLearners = 'English Learners'
const adaptedPhysicalEducation = 'Adapted Physical Education'
const speechAndLanguage = 'Speech & Language'
const transition = 'Transition'
export const reading = 'Reading'
const writing = 'Writing'
const math = 'Math'
const preAcademicSkills = 'Pre-Academic Skills'
const fineMotorSkills = 'Fine Motor Skills' /* Present Levels */
const grossMotorSkills = 'Gross Motor Skills' /* Present Levels */
const fineMotor = 'Fine Motor' /* Goals, Browse Assessments */
const grossMotor = 'Gross Motor' /* Goals, Browse Assessments */
const functional = 'Functional'
const communication = 'Communication'

const PLWSubjectsToGoalSubjectsMappings = {
  [preAcademicSkills]: [reading, math, preAcademics, fineMotor, autism, speechAndLanguage, executiveFunctioning],
  [reading]: [reading],
  [writing]: [writing],
  [math]: [math],
  'Alt Academics: Reading': [altAcademics],
  'Alt Academics: Writing': [altAcademics],
  'Alt Academics: Math': [altAcademics],
  [executiveFunctioning]: [executiveFunctioning, autism, preAcademics],
  Behavior: [behaviorSEL],
  'Social & Emotional Learning': [behaviorSEL],
   [communication]: [speechAndLanguage, autism, preAcademics, 'Assistive Technology'],
  [fineMotorSkills]: [fineMotor, preAcademics],
  [grossMotorSkills]: [grossMotor, preAcademics, adaptedPhysicalEducation],
  [functional]: [lifeSkills, autism, preAcademics, adaptedPhysicalEducation, transition],
  [transition]: [transition, autism, lifeSkills, executiveFunctioning, fineMotor]
};

// Present Levels Reading Skills
const conceptsOfPrint = 'Concepts of Print'
const phonologicalAwareness= 'Phonological Awareness'
const letterSoundIdentification = 'Letter/Sound Identification'
const highFrequencyWordReading = 'High Frequency Word Reading'
const phonicsAndDecoding = 'Phonics & Decoding'
const fluency = 'Fluency'
const comprehension = 'Comprehension'
const languageStructures = 'Language Structures'

// Mapped Reading Goal Domains
const vocabulary = 'Vocabulary'
const comprehensionLit = 'Comprehension: Literature'
const comprehensionInfo = 'Comprehension: Informational Text'

const PLWSkillsToGoalDomainsMappings = {
  [conceptsOfPrint]: [conceptsOfPrint],
  // Phonological Awareness is being split out of Phonics & Decoding/Vocabulary into its own goal domain;
  // keep the legacy fallback until existing goals are retagged (see Reading & Transition Category Updates project).
  [phonologicalAwareness]: [phonologicalAwareness, phonicsAndDecoding, vocabulary],
  [letterSoundIdentification]: [conceptsOfPrint, phonicsAndDecoding],
  [highFrequencyWordReading]: [phonicsAndDecoding, fluency, comprehensionInfo, comprehensionLit],
  [phonicsAndDecoding]: [phonicsAndDecoding, comprehensionInfo, comprehensionLit],
  [fluency]: [fluency, comprehensionInfo, comprehensionLit],
  [comprehension]: [comprehensionLit, comprehensionInfo],
  [languageStructures]: [languageStructures]
}

const noGradeFilterSubjectsForRelevantGoals = [preAcademicSkills, fineMotorSkills, grossMotorSkills, functional, executiveFunctioning, communication, transition]

const EnrolledGradeToGradeMapping = {
  'Pre-K': 'Pre-K',
  K: 'K',
  '1st': '1',
  '2nd': '2',
  '3rd': '3',
  '4th': '4',
  '5th': '5',
  '6th': '6',
  '7th': '7',
  '8th': '8',
  '9th': '9',
  '10th': '10',
  '11th': '11',
  '12th': '12',
}

const gradesSet = [
  'Pre-K',
  'K',
  '1st',
  '2nd',
  '3rd',
  '4th',
  '5th',
  '6th',
  '7th',
  '8th',
  '9th',
  '9-10th',
  '10th',
  '11th',
  '11-12th',
  '12th',
  'Mastery',
]

const enrolledGradesSet = [
  { name: 'Pre-Kindergarten', val: 'Pre-K' },
  { name: 'Kindergarten', val: 'K' },
  { name: '1st Grade', val: '1st' },
  { name: '2nd Grade', val: '2nd' },
  { name: '3rd Grade', val: '3rd' },
  { name: '4th Grade', val: '4th' },
  { name: '5th Grade', val: '5th' },
  { name: '6th Grade', val: '6th' },
  { name: '7th Grade', val: '7th' },
  { name: '8th Grade', val: '8th' },
  { name: '9th Grade', val: '9th' },
  { name: '10th Grade', val: '10th' },
  { name: '11th Grade', val: '11th' },
  { name: '12th Grade', val: '12th' },
]

// beta: true will hide the subject unless user activates beta mode
// dateAdded is used as a reference for whether to show a "new" tag next to the subject.
// NOTE: the month arg is 0-based number, but days are 1-based.
const areasOfNeed = [
  {
    label: 'Pre-Academic Skills',
    value: 'Pre-Academic Skills',
    beta: false,
    dateAdded: new Date(2020, 2, 12), // March 12, 2020 (not Feb 12, 2020)
  },
  {
    label: 'Reading',
    value: 'Reading',
    iodSubject: true,
  },
  {
    label: 'Writing',
    value: 'Writing',
    iodSubject: true,
  },
  {
    label: 'Math',
    value: 'Math',
    iodSubject: true,
  },
  {
    label: 'Alt Academics: Reading',
    value: 'Alt Academics: Reading',
    beta: false,
    dateAdded: new Date(2020, 2, 12),
  },
  {
    label: 'Alt Academics: Writing',
    value: 'Alt Academics: Writing',
    beta: false,
    dateAdded: new Date(2020, 10, 24),
  },
  {
    label: 'Alt Academics: Math',
    value: 'Alt Academics: Math',
    beta: false,
    dateAdded: new Date(2020, 2, 12),
  },
  {
    label: 'Executive Functioning',
    value: 'Executive Functioning',
  },
  {
    label: 'Behavior',
    value: 'Behavior',
  },
  {
    label: 'Social and Emotional Learning',
    value: 'Social & Emotional Learning',
  },
  {
    label: 'Communication',
    value: 'Communication',
  },
  {
    label: 'Fine Motor Skills',
    value: 'Fine Motor Skills',
  },
  {
    label: 'Gross Motor Skills',
    value: 'Gross Motor Skills',
    dateAdded: new Date(2020, 7, 21), // August 21, 2020
  },
  {
    label: 'Functional and Life Skills',
    value: 'Functional',
  },
  {
    label: 'Transition',
    value: 'Transition',
    beta: false,
    dateAdded: new Date(2020, 2, 12),
  },
]

function remapGoalSubject(subject) {
  return subject === 'Behavior' ? 'Behavior & SEL' : subject
}

function remapStrategySubject(subject) {
  return subject === 'Behavior' ? 'Behavior & SEL' : subject
}

function returnGoalSubjectForPLWSubject(subject) {
  return (
    Array.isArray(PLWSubjectsToGoalSubjectsMappings[subject]) &&
    PLWSubjectsToGoalSubjectsMappings[subject]
  )
}

function returnGoalDomainsForPLWSkill(skill) {
  let domains = PLWSkillsToGoalDomainsMappings[skill]
  return Array.isArray(domains) ? domains : []
}

function returnGradeFromEnrolledGrade(grade) {
  return EnrolledGradeToGradeMapping[grade]
}

function getInstructionalLevel(enrolledGrade, selectedGrade) {
  if (enrolledGrade === 'Pre-K') {
    enrolledGrade = -1
  } else if (enrolledGrade === 'K') {
    enrolledGrade = 0
  } else {
    enrolledGrade = parseInt(enrolledGrade.replace(/st|nd|rd|th/gi, ''))
  }

  if (selectedGrade === 'Pre-K') {
    selectedGrade = -1
  } else if (selectedGrade === 'K') {
    selectedGrade = 0
  } else {
    selectedGrade = parseInt(selectedGrade.replace(/st|nd|rd|th/gi, ''))
  }

  if (enrolledGrade - selectedGrade < 0) {
    return gradeInstructionalLevels.TWO_ABOVE_GRADE_LEVEL
  } else if (enrolledGrade === selectedGrade) {
    return gradeInstructionalLevels.AT_GRADE_LEVEL
  } else if (enrolledGrade - selectedGrade < 3) {
    return gradeInstructionalLevels.TWO_BELOW_GRADE_LEVEL
  }
  return gradeInstructionalLevels.THREE_BELOW_GRADE_LEVEL
}

function getAreasOfNeed(opts = { selBlocked: false, iodSubject: false, blockedSubjects: [] }) {
  let areasToReturn = areasOfNeed.slice()
  if (opts && opts.selBlocked) {
    areasToReturn.find((area) => area.value === SEL_DEFAULT_AREA_PLW).label =
      SEL_REPLACEMENT_STRATEGIES_PLW
  }
  if (opts && opts.iodSubject) {
    areasToReturn = areasToReturn.filter((area) => area.iodSubject)
  }
  if (opts && opts.blockedSubjects && opts.blockedSubjects.length > 0) {
    areasToReturn = areasToReturn.filter((area) => !opts.blockedSubjects.includes(area.value))
  }
  return areasToReturn
}

function getGradesSet() {
  return gradesSet.slice()
}

function getEnrolledGradesSet() {
  return enrolledGradesSet.slice()
}

function getGradeLabelForGradeLevel(gradeLevel) {
  let gradeOption = enrolledGradesSet.filter(({ val }) => gradeLevel === val)[0]
  if (gradeOption && gradeOption.name) {
    return gradeOption.name
  }
  return gradeLevel
}

function getSkillTypes() {
  return skillTypes
}

async function getSkillBySubject(subject, licenseSubType, contentProfile) {
  const options = {license_sub_type: licenseSubType, content_profile: contentProfile}
  const queryString = `subject=${encodeURIComponent(subject)}&` + new URLSearchParams(options).toString()
  const response = await fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/skills?${queryString}`, {
    method: 'GET',
    credentials: 'include',
  })
  if (response.ok) {
    try {
      const json = await response.json()
      return json
    } catch (error) {
      console.error('Problem getting skills json', error)
      return
    }
  } else {
    console.error('Problem getting skills', response)
    return
  }
}
async function getImpactBySubject(subject, licenseSubType, contentProfile) {
  const options = {license_sub_type: licenseSubType, content_profile: contentProfile}
  const queryString = `subject=${encodeURIComponent(subject)}&` + new URLSearchParams(options).toString()
  const response = await fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/impacts?${queryString}`, {
    method: 'GET',
    credentials: 'include',
  })
  if (response.ok) {
    try {
      const json = await response.json()
      return json
    } catch (error) {
      console.error('Problem getting skills json', error)
      return
    }
  } else {
    console.error('Problem getting skills', response)
    return
  }
}

async function getStatementsBySkill(skillUuid, contentProfile, licenseSubType, subject = null) {
  const options = {skill: skillUuid, content_profile: contentProfile}
  const queryString = subject ? new URLSearchParams(options).toString() + `&subject=${encodeURIComponent(subject)}&` : new URLSearchParams(options).toString()
  const response = await fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/statements?${queryString}`,
  {
    method: 'GET',
    credentials: 'include',
  })
  if (response.ok) {
    try {
      const json = await response.json()
      if (json.statements) {
        json.statements = lockStatementsByLicenseSubType(json.statements, licenseSubType)
      }
      return json
    } catch (error) {
      console.error('Problem getting statement json', error)
      return
    }
  } else {
    console.error('Problem getting statements', response)
    return
  }
}

async function getEffectStatementsBySubjectAndGrade(subject, grade) {
  const options = {grade}
  const queryString = `subject=${encodeURIComponent(subject)}&` +  new URLSearchParams(options).toString()
  const response = await fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/effects?${queryString}`, {
    method: 'GET',
    credentials: 'include',
  })
  if (response.ok) {
    try {
      const json = await response.json()
      return json
    } catch (error) {
      console.error('Problem getting effect statement json', error)
      return
    }
  } else {
    console.error('Problem getting effect statements', response)
    return
  }
}

async function getSkillForEffectStatement(subject, grade) {
  const response = await getEffectStatementsBySubjectAndGrade(subject, grade)
  if (response.statements && response.statements.length > 0) {
    var uuid = response.statements[0].skill_uuid
    var name = response.statements[0].skill_name
    return {
      subject,
      skills: [{
        locked: false,
        skill_name: name,
        skill_uuid: uuid,
        skill_order: '20',
        subject,
      }]
    }
  } else {
    return {
      subject,
      skills: [],
    }
  }
}

function lockStatementsByLicenseSubType(statements, licenseSubType) {
  switch (licenseSubType) {
    case LICENSE_SUB_TYPES.TRIAL:
      const sortedStatements = statements.slice().sort((a,b) => parseInt(a.level_order) - parseInt(b.level_order))
      if (sortedStatements[0].skill_type === 'skill') {
        // only the 3 highest levels available for skills:
        for (let i=(sortedStatements.length - 3); i >= 0; i--) {
          sortedStatements[i].locked = true
        }
        statements = sortedStatements
      } else if (!UNLOCKED_IMPACT_UUIDS.includes(sortedStatements[0].skill_uuid)) {
        // aside from specific impacts, all impact statements should be locked
        for (let i=0; i<statements.length; i++) {
          statements[i].locked = true
        }
      }
  }
  return statements
}

async function postPLStudent({
  uuid,
  userUuid,
  studentUuid,
  name,
  pronoun,
  enrolledGrade,
  subjects,
}) {
  const gbcs = getCookie('gbcs')
  return fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/student`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': gbcs,
      },
      body: JSON.stringify({
        uuid: uuid,
        user_uuid: userUuid,
        student_uuid: studentUuid,
        student_name: name,
        student_pronoun: pronoun,
        enrolled_grade_level: enrolledGrade,
        selected_subjects: subjects,
      }),
    })
}

async function postPresentLevelStudent({
  uuid,
  userUuid,
  studentUuid,
  name,
  pronoun,
  enrolledGrade,
  subjects,
}) {
  const handleRequest = getHandleRequest()
  return handleRequest({requestFn: postPLStudent}, {
    uuid,
    userUuid,
    studentUuid,
    name,
    pronoun,
    enrolledGrade,
    subjects,
  })
}

async function getPLStudentStatement(uuid, subject) {
  const gbcs = getCookie('gbcs')
  const encodedSubject = encodeURIComponent(subject);
  return fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/student/${uuid}?subject=${encodedSubject}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': gbcs,
      }
    })
}

async function getPresentLevelStudentStatement(uuid, subject) {
  const handleRequest = getHandleRequest()
  return handleRequest({requestFn: getPLStudentStatement}, uuid, subject)
}

async function postPLStudentStatement({
  uuid,
  statementUuid,
  subject,
  skillLevel,
  skillUuid,
  statementText,
  defaultStatement,
  multiSelect = false
}) {
  var data = {
    subject_name: subject,
    skill_name: skillLevel,
    skill_uuid: skillUuid,
    statement_text: statementText,
    selected_statement: defaultStatement,
    is_selected: true,
  }

  if (statementUuid) {
    data.uuid = statementUuid
  }
  const gbcs = getCookie('gbcs')
  return fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/student/${uuid}?multiselect=${multiSelect}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': gbcs,
      },
      body: JSON.stringify(data),
    })
}

async function postPresentLevelStudentStatement({
  uuid,
  statementUuid,
  subject,
  skillLevel,
  skillUuid,
  statementText,
  defaultStatement,
  multiSelect = false
}) {
  const handleRequest = getHandleRequest()
  return handleRequest({requestFn: postPLStudentStatement}, {uuid, statementUuid, subject, skillLevel, skillUuid, statementText, defaultStatement, multiSelect})
}

async function putPLStudentStatementSelectStatus(uuid, statementUuid, status) {
  const gbcs = getCookie('gbcs')
  return fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/student/${uuid}/deselect/${statementUuid}`,
    {
      method: 'PUT',
      crendentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': gbcs,
      },
      body: JSON.stringify({ is_selected: status }),
    })
}

async function putPresentLevelStudentStatementSelectStatus(uuid, statementUuid, status) {
  const handleRequest = getHandleRequest()
  return handleRequest({requestFn: putPLStudentStatementSelectStatus}, uuid, statementUuid, status)
}

async function deletePLStudentStatement(uuid) {
  const gbcs = getCookie('gbcs')
  return fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/student/${uuid}`,
    {
      method: 'DELETE',
      crendentials: 'include',
      headers: {
        'X-CSRF-Token': gbcs,
      },
    })
}

async function deletePresentLevelStudentStatement(uuid) {
  const handleRequest = getHandleRequest()
  return handleRequest({requestFn: deletePLStudentStatement}, uuid)
}

async function deleteAllPLStudentStatementsForSkill(plUuid, skillUuid) {
  const gbcs = getCookie('gbcs')
  return fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1//student/${plUuid}/skill/${skillUuid}`,
    {
      method: 'DELETE',
      crendetials: 'include',
      headers: {
        'X-CSRF-Token': gbcs,
      },
    })
}

async function deleteAllPresentLevelStudentStatementsForSkill(plUuid, skillUuid) {
  const handleRequest = getHandleRequest()
  return handleRequest({requestFn: deleteAllPLStudentStatementsForSkill}, plUuid, skillUuid)
}

async function getPLStudentStatementDraft(uuid, params = {}) {
  const gbcs = getCookie('gbcs')
  return fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/student/${uuid}/draft?${$.param(params)}`,
   {
    method: 'GET',
    headers: {
      'X-CSRF-Token': gbcs,
    },
    credentials: 'include'
  })
}

async function getPresentLevelStudentStatementDraft(uuid, params = {}) {
  const handleRequest = getHandleRequest()
  return handleRequest({requestFn: getPLStudentStatementDraft}, uuid, params);
}

function getSharedPresentLevelStudentStatement(uuid) {
  return $.ajax({
    url: `${toolkitUrls.api}/library/shared/presentlevels/${uuid}`,
    method: 'GET',
    dataType: 'json',
  })
}

function populatePlaceholderText(text, { studentName, studentGrade, dateStr }) {
  if (studentGrade) {
    text = text.replace(
      /\{enrolledGradeLevel\}/g,
      getGradeLabelForGradeLevel(studentGrade).toLowerCase()
    )
  }
  if (studentName) {
    text = text.replace(/\{student\}/g, studentName)
    text = text.replace(/\{pronoun\}/g, studentName)
  }

  if (dateStr) {
    text = text.replace(/\{date\}/g, dateStr)
  }
  return text
}

function subjectHasGradeFiltersForAlignedGoals(subject) {
  return noGradeFilterSubjectsForRelevantGoals.indexOf(subject) === -1
}
